import { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Check, Users, Loader2, Plus, Trash2, Baby, User, Calendar, MapPin, CreditCard, Copy, Phone, MessageCircle } from "lucide-react";
import api from "../lib/api";

// CONFIGURAZIONE: Impostare a false per bloccare le iscrizioni
const REGISTRATIONS_OPEN = false;
const CONTACT_PHONE = "+39 3928553492";
const CONTACT_NAME = "Flavio";

const PRICES = {
  adult: 190,
  child_first: 150,
  child_additional: 130,
  child_free: 0,
  sendme_kids: 15  // Programma SendMe Kids 3-10 anni (obbligatorio)
};

// Custom colors
const COLORS = {
  primary: "#ffd21f",    // Giallo
  secondary: "#a44214",  // Marrone/arancione
  dark: "#000000"        // Nero
};

export default function SendMeCampPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [totalPrice, setTotalPrice] = useState(PRICES.adult);
  const [registrationCode, setRegistrationCode] = useState("");
  const [depositAmount, setDepositAmount] = useState(50);
  const [totalParticipants, setTotalParticipants] = useState(1);
  
  const [formData, setFormData] = useState({
    name: "",
    surname: "",
    birth_date: "",
    gender: "",
    phone: "",
    church: "",
    email: "",
    room_preferences: "",
    notes: "",
  });

  const [familyMembers, setFamilyMembers] = useState([]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addFamilyMember = () => {
    const newMember = {
      id: Date.now(),
      name: "",
      surname: "",
      birth_date: "",
      gender: "",
      is_child: false,
      child_age_range: null,
      has_bed: true
    };
    const updated = [...familyMembers, newMember];
    setFamilyMembers(updated);
    recalculateTotal(updated);
  };

  const removeFamilyMember = (id) => {
    setFamilyMembers(prev => prev.filter(m => m.id !== id));
    recalculateTotal(familyMembers.filter(m => m.id !== id));
  };

  const updateFamilyMember = (id, field, value) => {
    const updated = familyMembers.map(m => {
      if (m.id === id) {
        const newMember = { ...m, [field]: value };
        if (field === "is_child" && !value) {
          newMember.child_age_range = null;
        }
        return newMember;
      }
      return m;
    });
    setFamilyMembers(updated);
    recalculateTotal(updated);
  };

  const recalculateTotal = (members) => {
    let total = PRICES.adult;
    let childCount = 0;
    
    members.forEach(m => {
      if (m.is_child && m.child_age_range === "0-2") {
        // Free - bambini 0-2 anni
      } else if (m.is_child && m.child_age_range === "3-10") {
        childCount++;
        // Costo bambino + programma SendMe Kids obbligatorio
        const childPrice = childCount === 1 ? PRICES.child_first : PRICES.child_additional;
        total += childPrice + PRICES.sendme_kids;
      } else {
        total += PRICES.adult;
      }
    });
    
    setTotalPrice(total);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.surname || !formData.birth_date || !formData.gender || 
        !formData.phone || !formData.church || !formData.email) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }

    for (const member of familyMembers) {
      if (!member.name || !member.surname || !member.birth_date || !member.gender) {
        toast.error("Compila tutti i dati dei familiari");
        return;
      }
      if (member.is_child && !member.child_age_range) {
        toast.error("Seleziona la fascia d'età per i bambini");
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        family_members: familyMembers.map(({ id, ...rest }) => rest)
      };
      const response = await api.post("/sendme-camp/registrations", payload);
      setTotalPrice(response.data.total_price);
      setRegistrationCode(response.data.registration_code);
      setDepositAmount(response.data.deposit_amount);
      setTotalParticipants(response.data.total_participants);
      setIsSuccess(true);
      toast.success("Iscrizione effettuata con successo!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'iscrizione");
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Codice copiato!");
  };

  // Schermata di blocco iscrizioni
  if (!REGISTRATIONS_OPEN) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: `linear-gradient(to bottom, ${COLORS.secondary}, ${COLORS.dark})` }}>
        <div className="max-w-lg w-full text-center">
          {/* Title */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold" style={{ color: COLORS.primary }}>
              SendME Conference 2026
            </h1>
          </div>

          {/* Message Box */}
          <div className="bg-black/60 border-2 rounded-2xl p-8 mb-8" style={{ borderColor: COLORS.primary }}>
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: COLORS.primary }}>
              <Users className="w-8 h-8 text-black" />
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-4">
              Iscrizioni Online Chiuse
            </h2>
            
            <p className="text-gray-300 text-lg mb-6">
              Per gli <span className="font-bold" style={{ color: COLORS.primary }}>ultimissimi posti</span> disponibili contatta:
            </p>
            
            <div className="bg-black/40 rounded-xl p-4 mb-6">
              <p className="text-xl font-bold text-white mb-1">{CONTACT_NAME}</p>
              <p className="text-lg" style={{ color: COLORS.primary }}>{CONTACT_PHONE}</p>
            </div>

            {/* WhatsApp Button */}
            <a
              href={`https://wa.me/${CONTACT_PHONE.replace(/[^0-9]/g, '')}?text=${encodeURIComponent("Ciao! Sono interessato agli ultimi posti disponibili per il SendME Conference 2026.")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-3 w-full py-4 px-6 rounded-xl text-white font-bold text-lg transition-all hover:scale-105 hover:shadow-lg"
              style={{ backgroundColor: "#25D366" }}
            >
              <MessageCircle className="w-6 h-6" />
              Contatta su WhatsApp
            </a>
          </div>

          {/* Footer */}
          <p className="text-gray-500 text-sm">
            1-4 Maggio 2026 • Villaggio Mareneve, Etna
          </p>
        </div>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: `linear-gradient(to bottom, ${COLORS.secondary}, ${COLORS.dark})` }}>
        <div className="max-w-lg w-full text-center">
          <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: COLORS.primary }}>
            <Check className="w-12 h-12 text-black" />
          </div>
          <h1 className="text-3xl font-bold mb-4" style={{ color: COLORS.primary }}>Iscrizione Confermata!</h1>
          <p className="text-gray-300 mb-6">
            Grazie per la tua iscrizione al SendME Conference 2026. Riceverai una conferma via email.
          </p>
          
          {/* Registration Code */}
          <div className="bg-black/60 border rounded-xl p-6 mb-6" style={{ borderColor: COLORS.primary }}>
            <p className="text-gray-400 text-sm mb-1">Il tuo codice iscrizione</p>
            <div className="flex items-center justify-center gap-3">
              <p className="text-4xl font-mono font-bold" style={{ color: COLORS.primary }}>
                {registrationCode}
              </p>
              <button 
                onClick={() => copyToClipboard(registrationCode)}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              >
                <Copy className="w-5 h-5 text-gray-400" />
              </button>
            </div>
          </div>

          <div className="bg-black/60 border border-gray-700 rounded-xl p-6 mb-6">
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <p className="text-gray-400 text-sm">Acconto da versare</p>
                <p className="text-sm text-gray-500">(€50 x {totalParticipants} {totalParticipants === 1 ? 'persona' : 'persone'})</p>
                <p className="text-3xl font-bold mt-1" style={{ color: COLORS.secondary }}>
                  €{depositAmount}
                </p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Totale da versare</p>
                <p className="text-sm text-gray-500">&nbsp;</p>
                <p className="text-3xl font-bold text-white mt-1">
                  €{totalPrice}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-black/60 border rounded-xl p-6 text-left" style={{ borderColor: COLORS.secondary }}>
            <h3 className="font-bold mb-3 flex items-center gap-2" style={{ color: COLORS.secondary }}>
              <CreditCard className="w-5 h-5" />
              Dati per il Bonifico
            </h3>
            <div className="space-y-2 text-sm text-gray-300">
              <p><span className="text-gray-500">IBAN:</span> IT93Z0306909606100000122881</p>
              <p><span className="text-gray-500">Intestato a:</span> Comunità del buon pastore</p>
              <p><span className="text-gray-500">Causale:</span> donazione liberale SendMe - {registrationCode}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: `linear-gradient(to bottom, ${COLORS.secondary}, ${COLORS.dark} 30%)` }}>
      {/* Header */}
      <div className="py-8 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_6292e840-6b8f-4ad9-807b-ea5b0fe1f71b/artifacts/d1r97ac5_logosend.png" 
            alt="SendME Conference Logo" 
            className="h-24 mx-auto mb-6"
          />
          <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ color: COLORS.primary }}>
            Iscrizione Conference 2026
          </h1>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-gray-300 text-sm">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" style={{ color: COLORS.primary }} />
              23-29 Agosto 2026
            </span>
            <span className="flex items-center gap-1">
              <MapPin className="w-4 h-4" style={{ color: COLORS.primary }} />
              Centro Ghilgal, Mondragone (CE)
            </span>
          </div>
        </div>
      </div>

      {/* Pricing Info */}
      <div className="max-w-2xl mx-auto px-4 mb-8">
        <div className="bg-black/60 rounded-xl p-4 border" style={{ borderColor: `${COLORS.primary}30` }}>
          <h3 className="font-bold mb-3 text-center" style={{ color: COLORS.primary }}>Tariffe</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center text-sm">
            <div className="bg-black/40 rounded-lg p-3">
              <div className="text-2xl font-bold text-white">€190</div>
              <div className="text-gray-400">Adulto</div>
            </div>
            <div className="bg-black/40 rounded-lg p-3">
              <div className="text-2xl font-bold text-white">€150</div>
              <div className="text-gray-400">1° figlio 4-10</div>
            </div>
            <div className="bg-black/40 rounded-lg p-3">
              <div className="text-2xl font-bold text-white">€130</div>
              <div className="text-gray-400">Dal 2° figlio</div>
            </div>
            <div className="bg-black/40 rounded-lg p-3">
              <div className="text-2xl font-bold" style={{ color: COLORS.secondary }}>Gratis</div>
              <div className="text-gray-400">0-3 anni*</div>
            </div>
          </div>
          <p className="text-xs text-gray-500 text-center mt-2">*senza posto letto</p>
          <p className="text-xs text-center mt-2" style={{ color: `${COLORS.primary}aa` }}>
            Sconti gruppo/famiglia disponibili - Contattaci per info!
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-2xl mx-auto px-4 pb-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* Personal Info */}
          <div className="bg-black/60 rounded-xl p-6 border border-zinc-800">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: COLORS.primary }}>
              <User className="w-5 h-5" />
              I Tuoi Dati
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-400">Nome *</Label>
                <Input
                  required
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  className="bg-black/50 border-zinc-700 text-white focus:border-yellow-400"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-400">Cognome *</Label>
                <Input
                  required
                  value={formData.surname}
                  onChange={(e) => handleChange("surname", e.target.value)}
                  className="bg-black/50 border-zinc-700 text-white focus:border-yellow-400"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-400">Data di Nascita *</Label>
                <Input
                  required
                  type="date"
                  value={formData.birth_date}
                  onChange={(e) => handleChange("birth_date", e.target.value)}
                  className="bg-black/50 border-zinc-700 text-white focus:border-yellow-400"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-400">Sesso *</Label>
                <RadioGroup
                  value={formData.gender}
                  onValueChange={(v) => handleChange("gender", v)}
                  className="flex gap-4 pt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="M" id="gender-m" className="border-yellow-400 text-yellow-400 data-[state=checked]:bg-yellow-400 data-[state=checked]:border-yellow-400" />
                    <Label htmlFor="gender-m" className={`cursor-pointer ${formData.gender === 'M' ? 'text-yellow-400 font-medium' : 'text-white'}`}>Maschio</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="F" id="gender-f" className="border-yellow-400 text-yellow-400 data-[state=checked]:bg-yellow-400 data-[state=checked]:border-yellow-400" />
                    <Label htmlFor="gender-f" className={`cursor-pointer ${formData.gender === 'F' ? 'text-yellow-400 font-medium' : 'text-white'}`}>Femmina</Label>
                  </div>
                </RadioGroup>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-400">Telefono *</Label>
                <Input
                  required
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  className="bg-black/50 border-zinc-700 text-white focus:border-yellow-400"
                  placeholder="+39 333 1234567"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-400">Chiesa *</Label>
                <Input
                  required
                  value={formData.church}
                  onChange={(e) => handleChange("church", e.target.value)}
                  className="bg-black/50 border-zinc-700 text-white focus:border-yellow-400"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label className="text-gray-400">Email *</Label>
                <Input
                  required
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  className="bg-black/50 border-zinc-700 text-white focus:border-yellow-400"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label className="text-gray-400">Preferenze compagni di stanza (2-3 nomi, senza impegno)</Label>
                <Textarea
                  value={formData.room_preferences}
                  onChange={(e) => handleChange("room_preferences", e.target.value)}
                  className="bg-black/50 border-zinc-700 text-white focus:border-yellow-400"
                  placeholder="Es: Mario Rossi, Luigi Verdi"
                  rows={2}
                />
              </div>
            </div>
          </div>

          {/* Family Members */}
          <div className="bg-black/60 rounded-xl p-6 border border-zinc-800">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2" style={{ color: COLORS.primary }}>
                <Users className="w-5 h-5" />
                Iscrizione Familiare
              </h2>
              <Button
                type="button"
                onClick={addFamilyMember}
                variant="outline"
                size="sm"
                className="border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-black"
              >
                <Plus className="w-4 h-4 mr-1" />
                Aggiungi
              </Button>
            </div>
            
            {familyMembers.length === 0 ? (
              <p className="text-gray-500 text-center py-4">
                Nessun familiare aggiunto. Clicca "Aggiungi" per iscrivere altri membri della famiglia.
              </p>
            ) : (
              <div className="space-y-4">
                {familyMembers.map((member, index) => (
                  <div key={member.id} className="bg-black/40 rounded-lg p-4 border border-zinc-700">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-medium" style={{ color: COLORS.primary }}>Familiare #{index + 1}</span>
                      <Button
                        type="button"
                        onClick={() => removeFamilyMember(member.id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Input
                        placeholder="Nome *"
                        value={member.name}
                        onChange={(e) => updateFamilyMember(member.id, "name", e.target.value)}
                        className="bg-black/50 border-zinc-700 text-white"
                      />
                      <Input
                        placeholder="Cognome *"
                        value={member.surname}
                        onChange={(e) => updateFamilyMember(member.id, "surname", e.target.value)}
                        className="bg-black/50 border-zinc-700 text-white"
                      />
                      <Input
                        type="date"
                        placeholder="Data di nascita *"
                        value={member.birth_date}
                        onChange={(e) => updateFamilyMember(member.id, "birth_date", e.target.value)}
                        className="bg-black/50 border-zinc-700 text-white"
                      />
                      <RadioGroup
                        value={member.gender}
                        onValueChange={(v) => updateFamilyMember(member.id, "gender", v)}
                        className="flex gap-4 items-center"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="M" id={`gender-m-${member.id}`} className="border-yellow-400 text-yellow-400 data-[state=checked]:bg-yellow-400 data-[state=checked]:border-yellow-400" />
                          <Label htmlFor={`gender-m-${member.id}`} className={`text-sm cursor-pointer ${member.gender === 'M' ? 'text-yellow-400 font-medium' : 'text-white'}`}>M</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="F" id={`gender-f-${member.id}`} className="border-yellow-400 text-yellow-400 data-[state=checked]:bg-yellow-400 data-[state=checked]:border-yellow-400" />
                          <Label htmlFor={`gender-f-${member.id}`} className={`text-sm cursor-pointer ${member.gender === 'F' ? 'text-yellow-400 font-medium' : 'text-white'}`}>F</Label>
                        </div>
                      </RadioGroup>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-4">
                      <label className="flex items-center gap-2 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={member.is_child}
                          onChange={(e) => updateFamilyMember(member.id, "is_child", e.target.checked)}
                          className="w-5 h-5 rounded border-2 border-zinc-600 bg-black/50 checked:bg-yellow-400 checked:border-yellow-400 focus:ring-yellow-400 focus:ring-offset-0 accent-yellow-400"
                          style={{ accentColor: '#ffd21f' }}
                        />
                        <span className={`text-sm flex items-center gap-1 ${member.is_child ? 'text-yellow-400 font-medium' : 'text-gray-300'}`}>
                          <Baby className="w-4 h-4" /> Bambino/a
                        </span>
                      </label>
                      {member.is_child && (
                        <div className="flex flex-col gap-2">
                          <RadioGroup
                            value={member.child_age_range || ""}
                            onValueChange={(v) => updateFamilyMember(member.id, "child_age_range", v)}
                            className="flex gap-3"
                          >
                            <div className="flex items-center space-x-1">
                              <RadioGroupItem value="0-2" id={`age-02-${member.id}`} className="border-yellow-400 text-yellow-400 data-[state=checked]:bg-yellow-400 data-[state=checked]:border-yellow-400" />
                              <Label htmlFor={`age-02-${member.id}`} className="text-sm cursor-pointer" style={{ color: COLORS.secondary }}>0-2 anni (gratis)</Label>
                            </div>
                            <div className="flex items-center space-x-1">
                              <RadioGroupItem value="3-10" id={`age-310-${member.id}`} className="border-yellow-400 text-yellow-400 data-[state=checked]:bg-yellow-400 data-[state=checked]:border-yellow-400" />
                              <Label htmlFor={`age-310-${member.id}`} className="text-white text-sm cursor-pointer">3-10 anni</Label>
                            </div>
                          </RadioGroup>
                          {member.child_age_range === "3-10" && (
                            <div className="bg-yellow-400/10 border border-yellow-400/30 rounded-lg p-3 mt-2">
                              <div className="flex items-center gap-2 text-yellow-400 font-medium text-sm">
                                <span>🎁</span>
                                <span>Programma SendMe Kids (obbligatorio) - €15</span>
                              </div>
                              <p className="text-xs text-gray-400 mt-1">
                                Include: T-shirt e cappellino in omaggio!
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="bg-black/60 rounded-xl p-6 border border-zinc-800">
            <Label className="text-gray-400">Note aggiuntive (allergie, esigenze particolari, ecc.)</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
              className="bg-black/50 border-zinc-700 text-white focus:border-yellow-400 mt-2"
              rows={3}
            />
          </div>

          {/* Price Summary */}
          <div className="rounded-xl p-6 border" style={{ background: `linear-gradient(to right, ${COLORS.primary}20, ${COLORS.secondary}20)`, borderColor: `${COLORS.primary}50` }}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-gray-400 text-sm">Partecipanti</div>
                <div className="text-2xl font-bold text-white">{1 + familyMembers.length}</div>
              </div>
              <div>
                <div className="text-gray-400 text-sm">Acconto da versare</div>
                <div className="text-xs text-gray-500">(€50 x {1 + familyMembers.length} {1 + familyMembers.length === 1 ? 'persona' : 'persone'})</div>
                <div className="text-2xl font-bold" style={{ color: COLORS.secondary }}>€{(1 + familyMembers.length) * 50}</div>
              </div>
              <div>
                <div className="text-gray-400 text-sm">Totale</div>
                <div className="text-xs text-gray-500">&nbsp;</div>
                <div className="text-2xl font-bold" style={{ color: COLORS.primary }}>€{totalPrice}</div>
              </div>
            </div>
          </div>

          {/* Payment Info */}
          <div className="bg-black/60 rounded-xl p-4 border" style={{ borderColor: `${COLORS.secondary}50` }}>
            <h3 className="font-bold mb-2 flex items-center gap-2" style={{ color: COLORS.secondary }}>
              <CreditCard className="w-4 h-4" />
              Pagamento tramite Bonifico Bancario
            </h3>
            <div className="text-sm text-gray-300 space-y-1">
              <p><span className="text-gray-500">IBAN:</span> IT93Z0306909606100000122881</p>
              <p><span className="text-gray-500">Intestato a:</span> Comunità del buon pastore</p>
              <p><span className="text-gray-500">Causale:</span> donazione liberale SendMe</p>
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full h-14 text-black font-bold text-lg rounded-xl transition-all hover:scale-[1.02]"
            style={{ backgroundColor: COLORS.primary }}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Invio in corso...
              </>
            ) : (
              "Conferma Iscrizione"
            )}
          </Button>

          <p className="text-center text-gray-500 text-sm">
            Confermando accetti i termini e le condizioni dell'evento.
          </p>
        </form>
      </div>

      {/* WhatsApp Support Button */}
      <div className="fixed bottom-5 right-5 z-[9999] flex flex-col items-center gap-2">
        <span className="text-black text-xs font-bold px-3 py-1 rounded-full shadow-lg" style={{ backgroundColor: COLORS.primary }}>
          PER INFO
        </span>
        <a
          href="https://wa.me/393928553492?text=Ho%20bisogno%20di%20info%20sul%20SendME%20Conference%202026"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center w-14 h-14 bg-[#25D366] rounded-full shadow-lg hover:scale-110 transition-transform"
          title="Contattaci su WhatsApp"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="white">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        </a>
      </div>
    </div>
  );
}
